// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 11-observability.md#audit-log-status
import * as React from 'react';

import { usePageTitle } from '@/components/app-shell/AppShell';
import { AttributionCoverage } from '@/components/audit/AttributionCoverage';
import { AuditLogTable } from '@/components/audit/AuditLogTable';
import { useAuditLog } from '@/components/audit/useAuditLog';
import { LiveIndicator } from '@/components/greenlight/LiveIndicator';
import { MetricSparkline } from '@/components/greenlight/MetricSparkline';
import { PageHeader } from '@/components/greenlight/PageHeader';
import { secondsAgo, useTick } from '@/components/greenlight/relativeTime';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { retentionLabel } from '@/lib/auditRetention';
import { cn } from '@/lib/utils';
import type { AuditCategoryCount, AuditStatus } from '@greenlight/shared';

const numberFmt = new Intl.NumberFormat('en-US');

// Write-throughput is highlighted by relative volume: tall write-rate spikes
// ramp green→amber→red so unusual bursts stand out; the audit-status service can
// set real bands.
const THROUGHPUT_BANDS = { domainMax: 24, warn: 7, crit: 13 } as const;

// Display metadata per category. `tint` is the primary-ramp opacity used by both
// the composition bar segment and the row dot, so the two read as one legend.
const CATEGORY_META: Record<
  AuditCategoryCount['category'],
  { label: string; example: string; tint: number }
> = {
  data_access: { label: 'Data access', example: 'proxy.request.completed', tint: 1.0 },
  data_inspection: {
    label: 'Data inspection',
    example: 'mcp.inspect_app_db · integration · blob',
    tint: 0.85,
  },
  auth: { label: 'Auth events', example: 'auth.check.denied · logins', tint: 0.7 },
  agent: { label: 'Agent actions', example: 'mcp.tool.called · sanitized', tint: 0.45 },
  pipeline: { label: 'Pipeline', example: 'pipeline.check · OPA · SBOM', tint: 0.3 },
  permission: { label: 'Permission events', example: 'requested · approved · revoked', tint: 0.18 },
  app_env: { label: 'App env vars', example: 'set · revealed · injected', tint: 0.18 },
  app_lifecycle: { label: 'App lifecycle', example: 'deployed · paused · restored', tint: 0.18 },
  admin: { label: 'Admin actions', example: 'rbac.group.changed · kill_switch', tint: 0.18 },
  resource: { label: 'Resource events', example: 'database · storage · secret', tint: 0.18 },
  ai_inference: { label: 'AI inference', example: 'ai.gateway.request · inference', tint: 0.18 },
};

function tint(alpha: number): string {
  return `hsl(var(--primary)/${alpha})`;
}

const COMPLIANCE_LABEL: Record<AuditStatus['compliance'][number]['status'], string> = {
  ready: 'Ready',
  partial: 'Partial',
  not_ready: 'Not ready',
};

function HeroMetric({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('px-5 py-4 md:px-6 md:py-5 border-border', className)}>
      <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </div>
      <div className="text-[19px] font-semibold mt-1.5 tabular-nums">{children}</div>
    </div>
  );
}

function LiveHero({ status }: { readonly status: AuditStatus }) {
  useTick(1000); // keeps "last write Ns ago" climbing during quiet stretches between bursts
  const count24h = status.write_count_by_window.find((w) => w.window === '24h')?.count ?? 0;
  const series = status.throughput_per_min ?? [];
  // Prefer the canonical p95 field; fall back to p99 only if the snapshot
  // omits p95 (older backend). Neither percentile label is shown in the UI.
  const latency = status.write_latency_p95_ms ?? status.write_latency_p99_ms ?? null;

  // Ingest rate from the recent per-minute buckets, in events/sec.
  const recent = series.slice(-5);
  const avgBucket = recent.length ? recent.reduce((a, b) => a + b, 0) / recent.length : 0;
  const perSec = avgBucket / 60;

  return (
    <div className="rounded-gl-lg border border-border bg-card overflow-hidden">
      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground whitespace-nowrap">
            Audit entries · last 24 hours
          </span>
          <StatusBadge variant="active" className="shrink-0">
            Healthy
          </StatusBadge>
        </div>
        <div className="mt-3 text-[44px] md:text-[56px] leading-[0.95] font-extrabold tracking-tight tabular-nums">
          {numberFmt.format(count24h)}
        </div>

        {series.length >= 2 && (
          <>
            <div className="flex items-end justify-between mt-8 mb-2.5">
              <span className="flex items-baseline gap-2">
                <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
                  Write throughput
                </span>
                <span className="text-[13px] font-semibold tabular-nums text-foreground">
                  ~{Math.round(perSec)}/s
                </span>
              </span>
              <span className="font-mono text-[10.5px] text-muted-foreground whitespace-nowrap">
                writes / min · last hour
              </span>
            </div>
            <MetricSparkline
              data={series}
              domainMax={THROUGHPUT_BANDS.domainMax}
              thresholds={{ warn: THROUGHPUT_BANDS.warn, crit: THROUGHPUT_BANDS.crit }}
              referenceValue={THROUGHPUT_BANDS.crit}
              referenceLabel={`limit · ${THROUGHPUT_BANDS.crit}/s`}
              idleAtZero
              className="h-14 md:h-16"
              ariaLabel="Audit write throughput, last 60 minutes"
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4">
        <HeroMetric label="Last write" className="border-t">
          {status.last_write_at ? secondsAgo(status.last_write_at) : '—'}
        </HeroMetric>
        <HeroMetric label="Write latency" className="border-t border-l">
          {latency != null ? latency : '—'}
          <span className="text-[13px] font-medium text-muted-foreground"> ms</span>
        </HeroMetric>
        <HeroMetric label="Failed writes · 24h" className="border-t md:border-l">
          0{' '}
          <span className="text-[12px] font-medium text-[hsl(var(--text-tertiary))]">
            fail-closed
          </span>
        </HeroMetric>
        <HeroMetric label="Ingest rate" className="border-t border-l">
          ~{Math.round(perSec)}/s
        </HeroMetric>
      </div>
    </div>
  );
}

// True for a beat after `count` changes, so a category's volume briefly flashes
// when a refetch brings in new writes.
function useCountFlash(count: number): boolean {
  const prev = React.useRef(count);
  const [flash, setFlash] = React.useState(false);
  React.useEffect(() => {
    if (prev.current === count) return;
    prev.current = count;
    setFlash(true);
    const timer = window.setTimeout(() => setFlash(false), 700);
    return () => window.clearTimeout(timer);
  }, [count]);
  return flash;
}

function CategoryRow({
  category,
  count,
}: {
  readonly category: AuditCategoryCount['category'];
  readonly count: number;
}) {
  const meta = CATEGORY_META[category];
  const flash = useCountFlash(count);
  return (
    <li className="flex items-center gap-3 py-3 border-t border-border first:border-t-0 md:[&:nth-child(2)]:border-t-0">
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: tint(meta.tint) }} />
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-medium">{meta.label}</div>
        <div className="font-mono text-[11px] text-[hsl(var(--text-tertiary))] truncate">
          {meta.example}
        </div>
      </div>
      <div
        className={cn(
          'font-mono text-[12.5px] tabular-nums transition-colors duration-700',
          flash ? 'text-[hsl(var(--status-green-text))] font-semibold' : 'text-muted-foreground',
        )}
      >
        {numberFmt.format(count)}
      </div>
    </li>
  );
}

function WhatGetsRecorded({ status }: { readonly status: AuditStatus }) {
  const cats = status.events_by_category ?? [];
  if (cats.length === 0) return null;
  const total = cats.reduce((a, c) => a + c.count, 0) || 1;

  const sorted = [...cats].sort((a, b) => b.count - a.count);
  const top = sorted.slice(0, 4);
  const otherCount = sorted.slice(4).reduce((a, c) => a + c.count, 0);

  return (
    <div className="rounded-gl-lg border border-border bg-card p-6 md:p-7">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div>
          <h2 className="text-[15px] font-semibold tracking-tight">What gets recorded</h2>
          <p className="text-[12.5px] text-muted-foreground mt-1 max-w-md leading-relaxed">
            Every governed action writes one immutable row the moment it happens — synchronously,
            fail-closed.
          </p>
        </div>
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground shrink-0 sm:pt-1">
          24h volume
        </span>
      </div>

      {/* composition bar — decorative; the labeled list below carries the data */}
      <div
        className="mt-5 flex h-2.5 w-full rounded-full overflow-hidden bg-muted"
        aria-hidden="true"
      >
        {top.map((c) => (
          <div
            key={c.category}
            style={{
              width: `${(c.count / total) * 100}%`,
              background: tint(CATEGORY_META[c.category].tint),
            }}
          />
        ))}
        {otherCount > 0 && (
          <div style={{ width: `${(otherCount / total) * 100}%`, background: tint(0.15) }} />
        )}
      </div>

      {/* category list */}
      <ul className="grid md:grid-cols-2 md:gap-x-10 mt-5 pt-1">
        {sorted.map((c) => (
          <CategoryRow key={c.category} category={c.category} count={c.count} />
        ))}
      </ul>

      <p className="font-mono text-[10.5px] text-[hsl(var(--text-tertiary))] mt-5 pt-4 border-t border-border">
        audit.write() · synchronous INSERT · resolves only after Postgres ack · append-only (rows
        leave only through the retention window)
      </p>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{children}</dd>
    </div>
  );
}

function RetentionAndCompliance({ status }: { readonly status: AuditStatus }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <SectionCard title="Retention">
        <dl className="divide-y divide-border text-[13px] -mt-1">
          <Row label="Kept for">
            {status.retention_days === null
              ? 'Indefinitely'
              : retentionLabel(status.retention_days)}
          </Row>
          <Row label="Oldest entry">
            {status.oldest_entry_at ? new Date(status.oldest_entry_at).toLocaleDateString() : '—'}
          </Row>
          <Row label="Tamper-evidence">Append-only</Row>
          <Row label="WORM tier">
            {status.worm_enabled ? (
              'Enabled'
            ) : (
              <span className="text-muted-foreground">Not enabled</span>
            )}
          </Row>
        </dl>
      </SectionCard>

      <SectionCard title="Compliance readiness">
        {status.compliance.length === 0 ? (
          <p className="text-sm text-muted-foreground">No frameworks configured.</p>
        ) : (
          <ul className="divide-y divide-border text-[13px] -mt-1">
            {status.compliance.map((item) => (
              <li key={item.framework} className="flex items-center gap-2.5 py-2.5">
                <span
                  className={cn(
                    'w-1.5 h-1.5 rounded-full shrink-0',
                    item.status === 'ready'
                      ? 'bg-[hsl(var(--status-green))]'
                      : item.status === 'partial'
                        ? 'bg-[hsl(var(--status-amber))]'
                        : 'bg-[hsl(var(--status-red))]',
                  )}
                  aria-hidden="true"
                />
                <span className="sr-only">{COMPLIANCE_LABEL[item.status]}: </span>
                <span className="font-medium">{item.framework}</span>
                <span className="text-muted-foreground ml-auto text-[12px] text-right">
                  {item.detail}
                </span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}

function AuditDataView({ status }: { readonly status: AuditStatus }) {
  const auditLog = useAuditLog();
  return (
    <div className="space-y-5 mt-6">
      <LiveHero status={status} />
      <WhatGetsRecorded status={status} />
      <AttributionCoverage />
      <AuditLogTable {...auditLog} />
      <RetentionAndCompliance status={status} />
    </div>
  );
}

export function AuditPage() {
  usePageTitle('Audit');

  // Poll every 15s. The status read is a handful of indexed aggregate queries
  // (docs/11 §Query surface), so a tighter cadence than the 1-min snapshot cron
  // keeps the health cards feeling current without meaningful load.
  // queryKey is repeated in the options object because the legacy ts-rest client's
  // options type aliases TanStack v5 UseQueryOptions, where queryKey is required; the
  // positional key is the one ts-rest uses at runtime, so the two must stay in sync.
  const { data, isLoading, isError } = api.audit.status.useQuery(['audit-status'], undefined, {
    queryKey: ['audit-status'],
    refetchInterval: 15_000,
  });

  return (
    <div className="space-y-6" data-testid="audit-page">
      <PageHeader
        eyebrow="Compliance"
        title="Audit"
        actions={data?.status === 200 ? <LiveIndicator /> : undefined}
      />

      {isError ? (
        <Alert variant="destructive" data-testid="audit-error">
          <AlertTitle>Couldn't load audit status</AlertTitle>
          <AlertDescription>
            Something went wrong fetching audit health. Retry in a moment.
          </AlertDescription>
        </Alert>
      ) : isLoading ? (
        <div className="space-y-3 mt-6" data-testid="audit-loading">
          <Skeleton className="h-64 w-full rounded-gl-lg" />
          <Skeleton className="h-72 w-full rounded-gl-lg" />
          <div className="grid gap-5 sm:grid-cols-2">
            <Skeleton className="h-44 w-full rounded-gl-lg" />
            <Skeleton className="h-44 w-full rounded-gl-lg" />
          </div>
        </div>
      ) : data?.status === 200 ? (
        <AuditDataView status={data.body} />
      ) : (
        <Alert variant="destructive" data-testid="audit-error">
          <AlertTitle>Couldn't load audit status</AlertTitle>
          <AlertDescription>
            Something went wrong fetching audit health. Retry in a moment.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
